import type { ChartSettings } from "../types";
import { defaultFontStack } from "./fonts";

export const defaultChartSettings: ChartSettings = {
  version: 4,
  scope: "global",
  chart: {
    view: "daily",
    range: "6M",
    height: 420,
    density: "standard",
    showLegend: true,
    showTooltip: true,
    showDataZoom: true,
    candleColorMode: "kr"
  },
  overlays: {
    priorHigh: true,
    cpLadder: true,
    sessionBackground: true,
    futuresBasis: false
  },
  movingAverages: {
    ma20: true,
    ma50: true,
    ma200: true,
    custom: false,
    customPeriod: 100
  },
  ui: {
    fontFamily: defaultFontStack
  },
  styles: {
    priorHigh: { color: "#cdd9e7", width: 1.2, type: "dashed" },
    trigger: { color: "#ff5d6e", width: 1.6, type: "dashed" },
    ladder: { color: "#0f73ff", width: 1.3, type: "dotted" },
    ma20: { color: "#ffc857", width: 1.8, type: "solid" },
    ma50: { color: "#24d18f", width: 1.8, type: "solid" },
    ma200: { color: "#9b4de3", width: 1.8, type: "solid" },
    customMa: { color: "#8bd3ff", width: 1.8, type: "dashed" },
    futuresBasis: { color: "#70f0c2", width: 1.4, type: "dashed" }
  }
};

export function mergeChartSettings(input?: Partial<ChartSettings> | null): ChartSettings {
  const isLegacySettings = Boolean(input && input.version !== defaultChartSettings.version);
  const safeCustomPeriod = input?.movingAverages?.customPeriod;
  const customPeriod = Number(safeCustomPeriod);
  const chartHeight = Number(input?.chart?.height);
  const normalizedChartHeight =
    isLegacySettings && (!Number.isFinite(chartHeight) || Math.round(chartHeight) === 320)
      ? defaultChartSettings.chart.height
      : chartHeight;
  const inputStyles = (input?.styles ?? {}) as Partial<ChartSettings["styles"]>;
  const styles = (Object.keys(defaultChartSettings.styles) as Array<keyof ChartSettings["styles"]>).reduce((acc, key) => {
    acc[key] = {
      ...defaultChartSettings.styles[key],
      ...(inputStyles[key] ?? {})
    };
    return acc;
  }, {} as ChartSettings["styles"]);

  return {
    version: defaultChartSettings.version,
    scope: input?.scope || defaultChartSettings.scope,
    chart: {
      view: input?.chart?.view ?? defaultChartSettings.chart.view,
      range: input?.chart?.range ?? defaultChartSettings.chart.range,
      height: Number.isFinite(normalizedChartHeight)
        ? Math.max(260, Math.min(Math.round(normalizedChartHeight), 780))
        : defaultChartSettings.chart.height,
      density: input?.chart?.density ?? defaultChartSettings.chart.density,
      showLegend: input?.chart?.showLegend ?? defaultChartSettings.chart.showLegend,
      showTooltip: input?.chart?.showTooltip ?? defaultChartSettings.chart.showTooltip,
      showDataZoom: input?.chart?.showDataZoom ?? defaultChartSettings.chart.showDataZoom,
      candleColorMode: input?.chart?.candleColorMode ?? defaultChartSettings.chart.candleColorMode
    },
    overlays: {
      ...defaultChartSettings.overlays,
      ...(input?.overlays ?? {}),
      futuresBasis: isLegacySettings
        ? false
        : input?.overlays?.futuresBasis ?? defaultChartSettings.overlays.futuresBasis
    },
    movingAverages: {
      ...defaultChartSettings.movingAverages,
      ...(input?.movingAverages ?? {}),
      customPeriod: Number.isFinite(customPeriod)
        ? Math.max(2, Math.min(Math.round(customPeriod), 500))
        : defaultChartSettings.movingAverages.customPeriod
    },
    ui: {
      ...defaultChartSettings.ui,
      ...(input?.ui ?? {}),
      fontFamily: input?.ui?.fontFamily || defaultChartSettings.ui.fontFamily
    },
    styles
  };
}
